import React from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek } from 'date-fns';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../../constants/brandPalette';

const CalendarView = ({ project, tasks, timeframe, onEditTask, onDeleteTask, currentDate }) => {
  const hasTasksInWeek = (weekDays) => {
    return weekDays.some(day => getTasksForDate(day).length > 0);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  
  // Get all days from previous month's monday to current month's last day sunday
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 0 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 0 })
  });

  // Group calendar days into weeks
  const weeks = calendarDays.reduce((acc, day, i) => {
    const weekIndex = Math.floor(i / 7);
    if (!acc[weekIndex]) {
      acc[weekIndex] = [];
    }
    acc[weekIndex].push(day);
    return acc;
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'done':
        return 'border-l-4 border-green-500 bg-green-100 text-green-800';
      case 'inProgress':
        return 'border-l-4 bg-blue-100 text-blue-800';
      case 'review':
        return 'border-l-4 border-amber-500 bg-amber-100 text-amber-800';
      case 'dispose':
        return 'border-l-4 border-red-500 bg-red-100 text-red-800';
      default:
        return 'border-l-4 border-gray-500 bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="text-green-500 flex-shrink-0" size={14} />;
      case 'inProgress':
        return <Clock className="text-blue-500 flex-shrink-0" size={14} />;
      case 'review':
        return <AlertCircle className="text-amber-500 flex-shrink-0" size={14} />;
      case 'dispose':
        return <AlertCircle className="text-red-500 flex-shrink-0" size={14} />;
      default:
        return <Clock className="text-gray-500 flex-shrink-0" size={14} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'done':
        return 'Done';
      case 'inProgress':
        return 'In Progress';
      case 'review':
        return 'Review';
      case 'todo':
        return 'To Do';
      case 'dispose':
        return 'Disposed';
      default:
        return 'To Do';
    }
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      const taskStart = new Date(task.startDate);
      const taskEnd = new Date(task.endDate || task.dueDate);
      const currentDate = new Date(date);
      
      // Reset time components for accurate date comparison
      taskStart.setHours(0, 0, 0, 0);
      taskEnd.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      
      return currentDate >= taskStart && currentDate <= taskEnd;
    });
  };

  return (
    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
      {/* Calendar Grid */}
      <div className="grid min-w-[840px] grid-cols-7">
        {/* Weekday Headers */}
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
          <div key={day} className="border-b py-1 text-center text-xs font-medium" style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
            {day}
          </div>
        ))}

        {/* Calendar Weeks */}
        {weeks.map((weekDays, weekIndex) => {
          const hasActivity = hasTasksInWeek(weekDays);
          
          return weekDays.map((day) => {
            const dayTasks = getTasksForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            
            return (
              <div
                key={day.toISOString()}
                className={`${hasActivity ? 'min-h-[100px]' : 'h-8'} border-b p-1 transition-all`}
                style={{
                  borderColor: SYSTEM_COLORS.borderSoft,
                  backgroundColor: isToday(day)
                    ? SYSTEM_COLORS.primaryTint
                    : (!isCurrentMonth ? SYSTEM_COLORS.surfaceSubtle : SYSTEM_COLORS.shellSurface),
                }}
              >
                <div className={`px-1 text-right text-xs ${isCurrentMonth ? 'font-medium' : ''}`} style={{ color: isCurrentMonth ? NEUTRAL_COLORS.black : SYSTEM_COLORS.textSoft }}>
                  {format(day, 'd')}
                </div>
                
                {hasActivity && (
                  <div className="space-y-0.5">
                    {dayTasks.map(task => (
                      <div
                        key={task.id}
                        className="group relative"
                      >
                        <div 
                          className={`${getStatusColor(task.status)} rounded px-1.5 py-0.5 text-xs shadow-sm group-hover:shadow transition-shadow cursor-pointer`}
                          style={{ borderLeftColor: task.status === 'inProgress' ? BRAND_COLORS.primary : undefined }}
                          onClick={() => onEditTask(task)}
                        >
                          <div className="flex items-center gap-1 truncate">
                            {getStatusIcon(task.status)}
                            <span className="truncate text-xs">{task.title}</span>
                          </div>

                          {/* Hover Info Card */}
                          <div className="pointer-events-none absolute left-0 -top-1 z-50 w-56 -translate-y-full rounded border p-2 text-sm opacity-0 shadow-lg transition-opacity group-hover:opacity-100" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                            <div className="mb-1 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{task.title}</div>
                            <div className="mb-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                              {format(new Date(task.startDate), 'MMM d')} - {format(new Date(task.endDate || task.dueDate), 'MMM d')}
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="font-medium">Stage: </span>
                              <span>{getStatusLabel(task.status)}</span>
                            </div>
                            {task.assignee && (
                              <div className="text-xs mt-0.5">
                                <span className="font-medium">Assigned: </span>
                                {task.assignee.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          });
        })}
      </div>
    </div>
  );
};

export default CalendarView; 